import React, { useState, useEffect } from 'react';
import {
    Button,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    DrawerFooter,
    DrawerCloseButton,
    FormControl,
    FormLabel,
    Input,
    Text,
    Textarea,
    HStack,
    VStack,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useToast,
    Portal,
    Box,
    Flex,
    Divider,
    Spinner,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { FiPlus } from "react-icons/fi";
import { useHistory, useLocation } from 'react-router-dom';
import { AddInventoryProducts } from "../../../InventoryServices/InventoryAPI/AddInventoryProducts";
import { GetInventoryServiceMap } from "../../../InventoryServices/InventoryServiceMapAPI/GetInventoryServiceMap";
import { AddInventorySource } from "../../../InventoryServices/InventorySourceAPI/InventorySource";
import { AddInventoryOutput } from "../../../InventoryServices/InventoryOutputAPI/InventoryOutput";

const AddProductButtonModal = () => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [productCreated, setProductCreated] = useState(false);
    const [createdProductId, setCreatedProductId] = useState(null);
    const [showSourceForm, setShowSourceForm] = useState(false);
    const [showOutputForm, setShowOutputForm] = useState(false);
    const [categories, setCategories] = useState([]);
    const toast = useToast();
    const history = useHistory();
    const location = useLocation();

    // Check if we're on board-details page
    const isBoardDetailsPage = location.pathname.includes('/board-details/');

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await GetInventoryServiceMap();
                if (res?.data?.data) {
                    setCategories(res.data.data);
                }
            } catch (error) {
                console.error("Failed to fetch categories:", error);
            }
        };
        fetchCategories();
    }, []);

    const [formData, setFormData] = useState({
        name: "",
        description: "",
        dropdownValue: ""
    });

    // Source form state
    const [sourceFormValues, setSourceFormValues] = useState({});
    const [sourceFile, setSourceFile] = useState(null);
    const [sourceLoading, setSourceLoading] = useState(false);

    // Output form state
    const [outputFile, setOutputFile] = useState(null);
    const [outputComment, setOutputComment] = useState("");
    const [outputLoading, setOutputLoading] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCategorySelect = (value) => {
        setFormData(prev => ({ ...prev, dropdownValue: value }));
    };

    const displayCategory = formData.dropdownValue
        ? (categories.find(c => c.service_code === formData.dropdownValue)?.service_name || formData.dropdownValue)
        : "Select category";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const fd = new FormData();
            fd.append("title", formData.name);
            fd.append("description", formData.description);
            fd.append("category", formData.dropdownValue);

            const res = await AddInventoryProducts(fd);

            if (res.status === 200 || res.status === 201) {
                const newId = res.data.data?.id;
                setCreatedProductId(newId);

                toast({
                    title: "Product added successfully!",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });

                // If on product-inventory page, redirect
                if (!isBoardDetailsPage) {
                    setIsDrawerOpen(false);
                    setFormData({ name: "", description: "", dropdownValue: "" });
                    history.push({
                        pathname: `/home/ap/product-inventory/tabs/${newId}`,
                        state: {
                            title: res.data.data?.title,
                            category: res.data.data?.category,
                        },
                    });
                } else {
                    // If on board-details page, show source/output forms
                    setProductCreated(true);
                }
            }

        } catch (error) {
            toast({
                title: "Failed to add product",
                description: error.response?.data?.message || "Something went wrong",
                status: "error",
                duration: 4000,
                isClosable: true,
            });

        } finally {
            setLoading(false);
        }
    };

    const handleTypeSelect = (type) => {
        setSourceFormValues((prev) => ({ ...prev, type }));
    };

    const displaySourceType = sourceFormValues.type || "Select Type";

    // Handle Source Add
    const handleSourceAdd = async () => {
        if (!createdProductId) {
            toast({
                title: "Product ID not found",
                status: "warning",
                duration: 2500,
                isClosable: true,
            });
            return;
        }

        try {
            setSourceLoading(true);

            const payload = new FormData();

            let apiType = "link";
            if (sourceFormValues.type === "Git") apiType = "giturl";
            else if (sourceFormValues.type === "Link") apiType = "link";
            else if (sourceFormValues.type === "Upload") apiType = "upload";

            payload.append("type", apiType);

            if (sourceFormValues.name) {
                payload.append("name", sourceFormValues.name);
            }
            if (sourceFormValues.comments) {
                payload.append("comments", sourceFormValues.comments);
            }

            if (apiType === "upload") {
                if (!sourceFile) {
                    toast({
                        title: "File required",
                        description: "Please select a file to upload.",
                        status: "warning",
                        duration: 2500,
                        isClosable: true,
                    });
                    setSourceLoading(false);
                    return;
                }
                payload.append("media", sourceFile);
            } else if (apiType === "link") {
                if (!sourceFormValues.value) {
                    toast({
                        title: "URL required",
                        description: "Please enter a valid link.",
                        status: "warning",
                        duration: 2500,
                        isClosable: true,
                    });
                    setSourceLoading(false);
                    return;
                }
                payload.append("link", sourceFormValues.value);
            } else if (apiType === "giturl") {
                if (!sourceFormValues.value) {
                    toast({
                        title: "Git URL required",
                        description: "Please enter a valid Git URL.",
                        status: "warning",
                        duration: 2500,
                        isClosable: true,
                    });
                    setSourceLoading(false);
                    return;
                }
                payload.append("git_url", sourceFormValues.value);
            }

            await AddInventorySource(createdProductId, payload);

            setSourceFormValues({});
            setSourceFile(null);
            setShowSourceForm(false);

            toast({
                title: "Source added successfully!",
                status: "success",
                duration: 2000,
                isClosable: true,
            });

        } catch (err) {
            console.error("Error adding source:", err);
            toast({
                title: "Failed to add source",
                description: err?.response?.data?.message || "Something went wrong",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setSourceLoading(false);
        }
    };

    // Handle Output Add
    const handleOutputAdd = async () => {
        if (!createdProductId) {
            toast({
                title: "Product ID not found",
                status: "warning",
                duration: 2500,
                isClosable: true,
            });
            return;
        }

        if (!outputFile) {
            toast({
                title: "File required",
                description: "Please select a file to upload.",
                status: "warning",
                duration: 2500,
                isClosable: true,
            });
            return;
        }

        try {
            setOutputLoading(true);
            const payload = new FormData();
            payload.append("media", outputFile);
            if (outputComment.trim()) {
                payload.append("comment", outputComment);
            }

            await AddInventoryOutput(createdProductId, payload);

            setOutputFile(null);
            setOutputComment("");
            setShowOutputForm(false);

            toast({
                title: "Output added successfully!",
                status: "success",
                duration: 2000,
                isClosable: true,
            });

        } catch (err) {
            console.error("Error adding output:", err);
            toast({
                title: "Failed to add output",
                description: err?.response?.data?.message || "Something went wrong",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setOutputLoading(false);
        }
    };

    const handleCloseDrawer = () => {
        setIsDrawerOpen(false);
        setProductCreated(false);
        setCreatedProductId(null);
        setShowSourceForm(false);
        setShowOutputForm(false);
        setFormData({ name: "", description: "", dropdownValue: "" });
        setSourceFormValues({});
        setSourceFile(null);
        setOutputFile(null);
        setOutputComment("");
    };

    return (
        <>
            {/* Button */}
            <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<FiPlus />}
                onClick={() => setIsDrawerOpen(true)}
            >
                Add Product
            </Button>

            {/* Drawer */}
            <Drawer 
                isOpen={isDrawerOpen} 
                placement="right" 
                onClose={handleCloseDrawer} 
                size="md"
            >
                <DrawerOverlay />
                <DrawerContent borderRadius="0" bg="white" shadow="xl">
                    <DrawerCloseButton mt={2} />
                    <DrawerHeader bg="blue.600" color="white" fontWeight="semibold" fontSize="lg">
                        {productCreated ? "Add Source & Output" : "Add New Product"}
                    </DrawerHeader>

                    <DrawerBody px={6} py={6}>
                        {!productCreated ? (
                            /* Product Form */
                            <form onSubmit={handleSubmit}>
                                <VStack spacing={6} align="stretch">
                                    <HStack spacing={4}>
                                        <FormControl>
                                            <FormLabel>Product Name</FormLabel>
                                            <Input
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Enter Product Name"
                                                bg="gray.50"
                                            />
                                        </FormControl>

                                        <FormControl>
                                            <FormLabel fontWeight="600">Category</FormLabel>
                                            <Menu isLazy closeOnSelect={true}>
                                                <MenuButton
                                                    as={Button}
                                                    rightIcon={<ChevronDownIcon />}
                                                    width="100%"
                                                    textAlign="left"
                                                    bg="gray.50"
                                                    border="1px solid"
                                                    borderColor="gray.300"
                                                    borderRadius="md"
                                                    cursor="pointer"
                                                    _hover={{ bg: "gray.100" }}
                                                    _active={{ bg: "gray.200" }}
                                                >
                                                    <Text color={formData.dropdownValue ? "gray.800" : "gray.500"}>
                                                        {displayCategory}
                                                    </Text>
                                                </MenuButton>

                                                <Portal>
                                                    <MenuList
                                                        maxHeight="250px"
                                                        overflowY="auto"
                                                        zIndex={999999}
                                                        border="1px solid"
                                                        borderColor="gray.300"
                                                        bg="white"
                                                    >
                                                        {categories.map((item) => (
                                                            <MenuItem
                                                                key={item.service_code}
                                                                onClick={() => handleCategorySelect(item.service_code)}
                                                                _hover={{ bg: "gray.100" }}
                                                            >
                                                                {item.service_name}
                                                            </MenuItem>
                                                        ))}
                                                    </MenuList>
                                                </Portal>
                                            </Menu>
                                        </FormControl>
                                    </HStack>

                                    <FormControl>
                                        <FormLabel>Description</FormLabel>
                                        <Textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Write a product description..."
                                            rows={4}
                                            bg="gray.50"
                                        />
                                    </FormControl>

                                    <Button type="submit" colorScheme="blue" isLoading={loading} width="full">
                                        Add Product
                                    </Button>
                                </VStack>
                            </form>
                        ) : (
                            /* Source and Output Forms */
                            <VStack spacing={6} align="stretch">
                                <Box 
                                    bg="green.50" 
                                    border="2px solid" 
                                    borderColor="green.300" 
                                    borderRadius="lg" 
                                    p={4}
                                    textAlign="center"
                                >
                                    <Text fontSize="2xl" mb={2}>✅</Text>
                                    <Text fontSize="lg" fontWeight="bold" color="green.700">
                                        Product Created Successfully!
                                    </Text>
                                    <Text fontSize="sm" color="gray.600" mt={1}>
                                        Now you can add source and output files
                                    </Text>
                                </Box>

                                <Divider />

                                {/* Add Source Section */}
                                <Box 
                                    border="2px solid" 
                                    borderColor="blue.100" 
                                    borderRadius="lg" 
                                    p={4}
                                    bg="blue.50"
                                >
                                    <Flex justify="space-between" align="center" mb={3}>
                                        <HStack>
                                            <Box 
                                                bg="blue.500" 
                                                color="white" 
                                                px={2} 
                                                py={1} 
                                                borderRadius="md"
                                                fontSize="xs"
                                                fontWeight="bold"
                                            >
                                                1
                                            </Box>
                                            <Text fontSize="md" fontWeight="bold" color="blue.700">
                                                Add Source
                                            </Text>
                                        </HStack>
                                        {!showSourceForm && (
                                            <Button 
                                                size="sm" 
                                                colorScheme="blue" 
                                                variant="solid"
                                                onClick={() => setShowSourceForm(true)}
                                                leftIcon={<FiPlus />}
                                            >
                                                Add Source
                                            </Button>
                                        )}
                                    </Flex>

                                    {showSourceForm && (
                                        <VStack spacing={4} align="stretch" p={4} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                                            <FormControl isRequired>
                                                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                                    Source Name
                                                </FormLabel>
                                                <Input
                                                    placeholder="Enter Source Name"
                                                    value={sourceFormValues.name || ""}
                                                    onChange={(e) =>
                                                        setSourceFormValues({ ...sourceFormValues, name: e.target.value })
                                                    }
                                                    bg="gray.50"
                                                />
                                            </FormControl>

                                            <FormControl isRequired>
                                                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                                    Source Type
                                                </FormLabel>
                                                <Menu autoSelect={false} closeOnSelect={true}>
                                                    <MenuButton
                                                        as={Button}
                                                        rightIcon={<ChevronDownIcon />}
                                                        textAlign="left"
                                                        bg="gray.50"
                                                        borderColor="gray.300"
                                                        borderWidth="1px"
                                                        _hover={{ bg: "gray.100" }}
                                                        width="100%"
                                                    >
                                                        {displaySourceType}
                                                    </MenuButton>
                                                    <Portal>
                                                        <MenuList zIndex="99999">
                                                            <MenuItem onClick={() => handleTypeSelect("Upload")}>📁 Upload File</MenuItem>
                                                            <MenuItem onClick={() => handleTypeSelect("Link")}>🌐 Web Link</MenuItem>
                                                        </MenuList>
                                                    </Portal>
                                                </Menu>
                                            </FormControl>

                                            <FormControl isRequired={sourceFormValues.type !== "Upload"}>
                                                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                                    {sourceFormValues.type === "Upload" ? "Upload File" : "Source URL"}
                                                </FormLabel>
                                                {sourceFormValues.type === "Upload" ? (
                                                    <Input
                                                        type="file"
                                                        onChange={(e) => setSourceFile(e.target.files[0])}
                                                        bg="gray.50"
                                                        pt={1}
                                                    />
                                                ) : (
                                                    <Input
                                                        placeholder={sourceFormValues.type === "Git" ? "https://github.com/..." : "https://..."}
                                                        value={sourceFormValues.value || ""}
                                                        onChange={(e) =>
                                                            setSourceFormValues({ ...sourceFormValues, value: e.target.value })
                                                        }
                                                        bg="gray.50"
                                                    />
                                                )}
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                                    Comments
                                                </FormLabel>
                                                <Textarea
                                                    placeholder="Add any additional notes..."
                                                    value={sourceFormValues.comments || ""}
                                                    onChange={(e) =>
                                                        setSourceFormValues({ ...sourceFormValues, comments: e.target.value })
                                                    }
                                                    bg="gray.50"
                                                    rows={3}
                                                />
                                            </FormControl>

                                            <HStack justify="flex-end" pt={2}>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => {
                                                        setShowSourceForm(false);
                                                        setSourceFormValues({});
                                                        setSourceFile(null);
                                                    }}
                                                    isDisabled={sourceLoading}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    colorScheme="blue"
                                                    onClick={handleSourceAdd}
                                                    isLoading={sourceLoading}
                                                    loadingText="Saving..."
                                                >
                                                    Save Source
                                                </Button>
                                            </HStack>
                                        </VStack>
                                    )}
                                </Box>

                                <Divider />

                                {/* Add Output Section */}
                                <Box 
                                    border="2px solid" 
                                    borderColor="green.100" 
                                    borderRadius="lg" 
                                    p={4}
                                    bg="green.50"
                                >
                                    <Flex justify="space-between" align="center" mb={3}>
                                        <HStack>
                                            <Box 
                                                bg="green.500" 
                                                color="white" 
                                                px={2} 
                                                py={1} 
                                                borderRadius="md"
                                                fontSize="xs"
                                                fontWeight="bold"
                                            >
                                                2
                                            </Box>
                                            <Text fontSize="md" fontWeight="bold" color="green.700">
                                                Add Output
                                            </Text>
                                        </HStack>
                                        {!showOutputForm && (
                                            <Button 
                                                size="sm" 
                                                colorScheme="green" 
                                                variant="solid"
                                                onClick={() => setShowOutputForm(true)}
                                                leftIcon={<FiPlus />}
                                            >
                                                Add Output
                                            </Button>
                                        )}
                                    </Flex>

                                    {showOutputForm && (
                                        <VStack spacing={4} align="stretch" p={4} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                                            <FormControl isRequired>
                                                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                                    Upload File
                                                </FormLabel>
                                                <Input
                                                    type="file"
                                                    onChange={(e) => setOutputFile(e.target.files[0])}
                                                    bg="gray.50"
                                                    pt={1}
                                                />
                                                {outputFile && (
                                                    <Text fontSize="xs" color="green.600" mt={1}>
                                                        ✓ {outputFile.name}
                                                    </Text>
                                                )}
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                                                    Comment
                                                </FormLabel>
                                                <Textarea
                                                    placeholder="Add any notes about this output..."
                                                    value={outputComment}
                                                    onChange={(e) => setOutputComment(e.target.value)}
                                                    bg="gray.50"
                                                    rows={3}
                                                />
                                            </FormControl>

                                            <HStack justify="flex-end" pt={2}>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => {
                                                        setShowOutputForm(false);
                                                        setOutputFile(null);
                                                        setOutputComment("");
                                                    }}
                                                    isDisabled={outputLoading}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    colorScheme="green"
                                                    onClick={handleOutputAdd}
                                                    isLoading={outputLoading}
                                                    isDisabled={!outputFile}
                                                    loadingText="Uploading..."
                                                >
                                                    Save Output
                                                </Button>
                                            </HStack>
                                        </VStack>
                                    )}
                                </Box>
                            </VStack>
                        )}
                    </DrawerBody>

                    {productCreated && (
                        <DrawerFooter bg="gray.50" borderTop="1px solid" borderColor="gray.200">
                            <VStack width="full" spacing={2}>
                                <Text fontSize="xs" color="gray.500">
                                    You can add sources and outputs now or click Done to finish
                                </Text>
                                <Button 
                                    colorScheme="blue" 
                                    onClick={handleCloseDrawer}
                                    width="full"
                                    size="lg"
                                >
                                    Done & Close
                                </Button>
                            </VStack>
                        </DrawerFooter>
                    )}
                </DrawerContent>
            </Drawer>
        </>
    );
};

export default AddProductButtonModal;